/**
 * cloudPickers — cross-platform "Import from Google Drive / Dropbox" helpers.
 *
 * On native (iOS/Android) the existing UX is to delegate to the system file
 * picker (`expo-document-picker`), which on iOS surfaces Drive/Dropbox as
 * locations inside the Files app. That keeps working unchanged.
 *
 * On web/desktop there is no Files-app equivalent, so this module loads the
 * official browser SDKs and runs the same flow the eleplan web app does
 * (see `frontend/client/src/Components/GoogleDrivePickerButton/index.js` and
 * the `react-dropbox-chooser` usage in `frontend/client/src/Pages/Documents/`):
 *
 *   • Google Drive — GIS OAuth → Google Picker → fetch each picked file via
 *     the Drive v3 API (export Google-native docs to Office formats) → return
 *     PickedFile[] with `blob:` URIs.
 *   • Dropbox      — Dropbox Chooser with linkType:'direct' → fetch the temp
 *     content link → return PickedFile[] with `blob:` URIs.
 *
 * The returned `PickedFile.uri` is a `blob:` URL. `uploadDocument` in
 * `store/actions/notes.ts` detects that prefix and PUTs the blob to the
 * presigned S3 URL via `fetch` instead of `FileSystem.uploadAsync` (which
 * doesn't exist on web), so the rest of the upload pipeline is unchanged
 * and the call sites in DocumentsScreen / ChatScreen / ProposedActionsCard
 * don't need to know what platform they're on.
 *
 * Origins must be allow-listed in the Google Cloud Console / Dropbox App
 * Console for the OAuth/chooser to authorize. Dev uses the same
 * localhost:3000 origin as the eleplan web app (which is already allow-listed),
 * so the credentials in `mobile/src/config.ts` work out of the box.
 */

import { Platform } from 'react-native';
import { pickFileDirect, type PickedFile } from './pickFile';
import config from '../config';
import debug from './debug';

const GOOGLE_GAPI_SCRIPT = 'https://apis.google.com/js/api.js';
const GOOGLE_GSI_SCRIPT = 'https://accounts.google.com/gsi/client';
const DROPBOX_DROPINS_SCRIPT = 'https://www.dropbox.com/static/api/2/dropins.js';

const GOOGLE_OAUTH_SCOPES = [
  'https://www.googleapis.com/auth/docs',
  'https://www.googleapis.com/auth/drive.file',
  // Identity (non-sensitive, no review) — the connect's id_token then carries the
  // account email + avatar so the import chooser/settings can show who's who.
  'openid',
  'email',
  'profile',
];

// Google-native MIME → preferred export MIME (matches the web widget so
// users get Office files instead of opaque Google blobs).
const GOOGLE_EXPORT_MIME: Record<string, string> = {
  'application/vnd.google-apps.document':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.google-apps.spreadsheet':
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.google-apps.presentation':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.google-apps.drawing': 'application/pdf',
};

const GOOGLE_EXPORT_EXT: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/pdf': '.pdf',
};

// Google MIME types we can't usefully export as a single file (folders,
// shortcuts, forms, sites). Skip during the fetch loop with a debug log.
const GOOGLE_SKIP_MIMES = new Set([
  'application/vnd.google-apps.form',
  'application/vnd.google-apps.folder',
  'application/vnd.google-apps.shortcut',
  'application/vnd.google-apps.site',
]);

const isGoogleNativeMime = (m: string | undefined | null) =>
  typeof m === 'string' && m.startsWith('application/vnd.google-apps');

// ─── DOM script loader ────────────────────────────────────────────────────
// Module-scoped promise cache so a second picker invocation reuses the
// already-loaded script tag instead of reinjecting (which Google's Picker
// API treats as a hard reset and breaks the in-flight token client).

const scriptPromises = new Map<string, Promise<void>>();

function injectScript(url: string, attrs?: Record<string, string>): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('cloudPickers: no document — not running in a browser environment'));
  }
  const cached = scriptPromises.get(url);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${url}"]`) as HTMLScriptElement | null;
    if (existing && (existing as any).__loaded) {
      resolve();
      return;
    }
    const el = existing || document.createElement('script');
    el.src = url;
    el.async = true;
    if (attrs) for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    el.addEventListener('load', () => {
      (el as any).__loaded = true;
      resolve();
    });
    el.addEventListener('error', () => {
      scriptPromises.delete(url);
      reject(new Error(`cloudPickers: failed to load ${url}`));
    });
    if (!existing) document.head.appendChild(el);
  });
  scriptPromises.set(url, promise);
  return promise;
}

// ─── Google Drive (web) ───────────────────────────────────────────────────

let pickerApiReady: Promise<void> | null = null;

function ensureGooglePickerApi(): Promise<void> {
  if (pickerApiReady) return pickerApiReady;
  pickerApiReady = (async () => {
    await Promise.all([
      injectScript(GOOGLE_GAPI_SCRIPT),
      injectScript(GOOGLE_GSI_SCRIPT),
    ]);
    // gapi loads itself onto window; need to call gapi.load('picker', cb)
    // separately to bring in the Picker namespace.
    await new Promise<void>((resolve, reject) => {
      const w = window as any;
      if (!w.gapi) return reject(new Error('cloudPickers: gapi missing after script load'));
      w.gapi.load('picker', { callback: () => resolve(), onerror: () => reject(new Error('cloudPickers: gapi.load(picker) failed')) });
    });
  })();
  return pickerApiReady;
}

function requestGoogleAccessToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (!w.google?.accounts?.oauth2?.initTokenClient) {
      reject(new Error('cloudPickers: Google Identity Services not loaded'));
      return;
    }
    const client = w.google.accounts.oauth2.initTokenClient({
      client_id: config.google.pickerClientId,
      scope: GOOGLE_OAUTH_SCOPES.join(' '),
      callback: (resp: any) => {
        if (resp?.error || !resp?.access_token) {
          reject(new Error(resp?.error || 'cloudPickers: no access_token in OAuth response'));
          return;
        }
        resolve(resp.access_token as string);
      },
      error_callback: (err: any) => {
        reject(new Error(err?.message || 'cloudPickers: Google OAuth flow was cancelled'));
      },
    });
    client.requestAccessToken();
  });
}

// ─── Persistent Drive connection (ED-2488) ─────────────────────────────────
// The implicit token flow above re-prompts every session. To connect ONCE we
// run the auth-code/offline flow: get a one-time auth CODE here, hand it to the
// backend (controllers/drive/connect), which stores a refresh token and mints
// access tokens silently thereafter (controllers/drive/token). Scope is
// unchanged (drive.file + docs) — no Google review. The Picker is unchanged;
// only how it gets its token changes.
//
// apiClient + session ids are lazy-required (not top-level imported) so this
// utility stays light at import time and doesn't pull the redux store into its
// module graph — same reasoning as store/actions/_sessionIds.
/* eslint-disable @typescript-eslint/no-require-imports */
function driveApiPost(path: string, body: any): Promise<any> {
  return require('./apiClient').apiPost(path, body);
}
function driveApiGet(path: string): Promise<any> {
  return require('./apiClient').apiGet(path);
}
function driveSessionIds(): { accountId?: string; cognitoId?: string } {
  return require('../store/actions/_sessionIds').getSessionIds();
}
/* eslint-enable @typescript-eslint/no-require-imports */

// "Which Drive?" chooser bridge (ED-2492). When more than one Drive is
// connected, the Picker asks which account to import from instead of forcing a
// single default. The chooser UI is a small modal mounted once at the app root
// (components/DriveAccountChooser) that registers itself here; if nothing is
// registered (e.g. mid-startup) the token flow degrades to the most-recently-
// used connection.
export interface DriveAccountOption {
  connection_id: number;
  google_email: string | null;
  google_picture?: string | null;
}
let _driveAccountChooser: ((accounts: DriveAccountOption[]) => Promise<number | null>) | null = null;
export function registerDriveAccountChooser(
  fn: ((accounts: DriveAccountOption[]) => Promise<number | null>) | null,
): void {
  _driveAccountChooser = fn;
}

// One-time consent: GIS auth-code popup → resolves the authorization code (or
// null if the user cancels or GIS is unavailable).
function requestGoogleAuthCode(): Promise<string | null> {
  return new Promise((resolve) => {
    const w = window as any;
    if (!w.google?.accounts?.oauth2?.initCodeClient) {
      resolve(null);
      return;
    }
    const client = w.google.accounts.oauth2.initCodeClient({
      client_id: config.google.pickerClientId,
      scope: GOOGLE_OAUTH_SCOPES.join(' '),
      ux_mode: 'popup',
      callback: (resp: any) => resolve(resp?.code && !resp?.error ? (resp.code as string) : null),
      error_callback: () => resolve(null),
    });
    client.requestCode();
  });
}

// Run the one-time connect: consent → exchange the code server-side for a stored
// refresh token. Exported so DriveSettingsScreen can "Connect another Drive";
// connecting a different Google account adds a new connection (multi-account).
export async function connectGoogleDriveAccount(): Promise<{ success: boolean; google_email?: string | null; message?: string }> {
  const { accountId, cognitoId } = driveSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not signed in.' };
  // The settings-screen "Connect another Drive" doesn't go through the document
  // Picker first, so the Google Identity SDK (initCodeClient) may not be loaded
  // yet. Load it on demand; surface a real error if it fails — otherwise the
  // button silently no-ops (ED-2493).
  if (!(window as any)?.google?.accounts?.oauth2?.initCodeClient) {
    try {
      await injectScript(GOOGLE_GSI_SCRIPT);
    } catch (err) {
      debug.error('cloudPickers: Google sign-in SDK failed to load', err as any);
      return { success: false, message: 'Could not load Google sign-in. Check your connection and try again.' };
    }
  }
  const code = await requestGoogleAuthCode();
  if (!code) return { success: false, message: 'cancelled' }; // user cancelled
  try {
    const res = await driveApiPost(`/drive/connect/${accountId}/${cognitoId}`, { code });
    return { success: !!(res && res.success && res.connected), google_email: res?.google_email ?? null };
  } catch (err) {
    debug.error('cloudPickers: Drive connect failed', err as any);
    return { success: false, message: 'Connection failed.' };
  }
}

// Obtain a Drive access token for the Picker. Prefers the persistent backend
// token (no re-consent); connects once on first use or when the stored grant
// was revoked (the mint endpoint returns 409). Falls back to the legacy
// implicit flow when the user has no eleplan session or the backend is
// unreachable, so picking still works (degraded: may re-prompt) — i.e. this is
// non-breaking even before the backend is deployed. Returns null only when the
// user actively cancels the consent popup.
async function getDriveAccessTokenViaBackend(): Promise<string | null> {
  const { accountId, cognitoId } = driveSessionIds();
  if (!accountId || !cognitoId) {
    // Not signed in to eleplan — can't use the persistent connection.
    return requestGoogleAccessToken();
  }
  const mint = (connectionId: number | null) =>
    driveApiPost(`/drive/token/${accountId}/${cognitoId}`, connectionId ? { connection_id: connectionId } : {});
  try {
    // Which Drives are connected for this account?
    const status = await driveApiGet(`/drive/status/${accountId}/${cognitoId}`);
    const connections: DriveAccountOption[] = Array.isArray(status?.connections) ? status.connections : [];

    if (connections.length === 0) {
      // Nothing connected → one-time connect, then mint.
      const connected = await connectGoogleDriveAccount();
      if (!connected.success) return null; // user cancelled consent
      const res = await mint(null);
      return res?.success && res.access_token ? (res.access_token as string) : null;
    }

    // Pick which Drive to import from: exactly one → use it (no prompt, unchanged
    // UX); more than one → ask the chooser; dismissed → no import.
    let connectionId: number | null = connections[0].connection_id;
    if (connections.length > 1) {
      if (_driveAccountChooser) {
        const chosen = await _driveAccountChooser(connections);
        if (chosen == null) return null; // user dismissed the chooser
        connectionId = chosen;
      } else {
        connectionId = null; // no chooser mounted → most-recent (backend default)
      }
    }

    let res = await mint(connectionId);
    if (res?.success && res.access_token) return res.access_token as string;
    // 409 = the chosen grant was revoked → reconnect, then retry.
    if (res?.status === 409) {
      const connected = await connectGoogleDriveAccount();
      if (!connected.success) return null;
      res = await mint(null);
      if (res?.success && res.access_token) return res.access_token as string;
    }
  } catch (err) {
    debug.warn('cloudPickers: Drive token flow failed; falling back to implicit flow', err as any);
  }
  // Transient / backend failure → degrade to the legacy implicit token flow.
  return requestGoogleAccessToken();
}

interface GooglePickerDoc {
  id: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number;
}

function openGooglePicker(token: string, multiselect: boolean): Promise<GooglePickerDoc[]> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    const picker = w.google?.picker;
    if (!picker) {
      reject(new Error('cloudPickers: Google Picker namespace missing'));
      return;
    }

    const view = new picker.DocsView(picker.ViewId.DOCS);
    // Thumbnail grid reads better than the cramped row list for picking files
    // (ED-2495); guard in case the loaded Picker build predates DocsViewMode.
    if (picker.DocsViewMode?.GRID) view.setMode(picker.DocsViewMode.GRID);
    const builder = new picker.PickerBuilder()
      .setOAuthToken(token)
      .setDeveloperKey(config.google.pickerApiKey)
      .setLocale('en')
      .setTitle('Import from Google Drive')
      .setOrigin(window.location.origin)
      .addView(view)
      .setCallback((data: any) => {
        if (data.action === picker.Action.PICKED) {
          resolve(Array.isArray(data.docs) ? data.docs : []);
        } else if (data.action === picker.Action.CANCEL) {
          resolve([]);
        }
      });

    if (multiselect) {
      builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
    }

    builder.build().setVisible(true);
  });
}

async function fetchGoogleDriveDoc(
  doc: GooglePickerDoc,
  token: string,
): Promise<PickedFile | null> {
  const mt = doc.mimeType || '';
  if (GOOGLE_SKIP_MIMES.has(mt)) {
    debug.log('documents', 'Skipping unsupported Google Drive file type', { mimeType: mt, name: doc.name });
    return null;
  }
  let url: string;
  let resultMime: string;
  if (isGoogleNativeMime(mt)) {
    resultMime = GOOGLE_EXPORT_MIME[mt] || 'application/pdf';
    url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(doc.id)}/export?mimeType=${encodeURIComponent(resultMime)}`;
  } else {
    resultMime = mt || 'application/octet-stream';
    url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(doc.id)}?alt=media`;
  }

  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) {
    throw new Error(`Drive fetch failed (${resp.status}) for ${doc.name || doc.id}`);
  }
  const blob = await resp.blob();
  // Some endpoints return application/octet-stream for non-Google natives —
  // fall back to whatever the API reported as the source MIME so we don't
  // lose type info for the upload pipeline.
  const finalMime = isGoogleNativeMime(mt) ? resultMime : (blob.type || resultMime);

  let name = doc.name || `drive-file-${doc.id}`;
  const desiredExt = GOOGLE_EXPORT_EXT[finalMime];
  if (desiredExt && !name.toLowerCase().endsWith(desiredExt)) name += desiredExt;

  return {
    uri: URL.createObjectURL(blob),
    name,
    mimeType: finalMime,
    size: blob.size,
  };
}

async function pickFromGoogleDriveWeb(allowMultiple: boolean): Promise<PickedFile[] | null> {
  if (!config.google.pickerApiKey || !config.google.pickerClientId) {
    debug.warn('cloudPickers: Google Picker API key/client id not configured');
    return null;
  }
  try {
    await ensureGooglePickerApi();
    const token = await getDriveAccessTokenViaBackend();
    if (!token) return null; // user cancelled the consent popup
    const docs = await openGooglePicker(token, allowMultiple);
    if (!docs.length) return null;
    const results: PickedFile[] = [];
    for (const doc of docs) {
      try {
        const file = await fetchGoogleDriveDoc(doc, token);
        if (file) results.push(file);
      } catch (err) {
        debug.error('cloudPickers: failed to download Google Drive file', err as any);
      }
    }
    return results.length ? results : null;
  } catch (err) {
    debug.error('cloudPickers: Google Drive picker failed', err as any);
    return null;
  }
}

// ─── Dropbox (web) ────────────────────────────────────────────────────────

interface DropboxChosenFile {
  name: string;
  link: string;       // signed temporary content URL when linkType === 'direct'
  bytes: number;
  isDir?: boolean;
}

async function ensureDropboxScript(): Promise<void> {
  await injectScript(DROPBOX_DROPINS_SCRIPT, { id: 'dropboxjs', 'data-app-key': config.dropbox.appKey });
}

function chooseFromDropbox(allowMultiple: boolean): Promise<DropboxChosenFile[]> {
  return new Promise((resolve) => {
    const w = window as any;
    if (!w.Dropbox?.choose) {
      resolve([]);
      return;
    }
    let settled = false;
    w.Dropbox.choose({
      success: (files: DropboxChosenFile[]) => {
        if (settled) return;
        settled = true;
        resolve(Array.isArray(files) ? files : []);
      },
      cancel: () => {
        if (settled) return;
        settled = true;
        resolve([]);
      },
      linkType: 'direct',
      multiselect: allowMultiple,
      // No `extensions` filter — accept anything, mirrors the web widget.
    });
  });
}

async function fetchDropboxFile(file: DropboxChosenFile): Promise<PickedFile | null> {
  if (file.isDir) return null;
  const resp = await fetch(file.link);
  if (!resp.ok) throw new Error(`Dropbox fetch failed (${resp.status}) for ${file.name}`);
  const blob = await resp.blob();
  return {
    uri: URL.createObjectURL(blob),
    name: file.name,
    mimeType: blob.type || 'application/octet-stream',
    size: file.bytes ?? blob.size,
  };
}

async function pickFromDropboxWeb(allowMultiple: boolean): Promise<PickedFile[] | null> {
  if (!config.dropbox.appKey) {
    debug.warn('cloudPickers: Dropbox app key not configured');
    return null;
  }
  try {
    await ensureDropboxScript();
    // The Dropbox Chooser's `choose()` requires being called inside a
    // user-activation window (popup blocker). Callers invoke this from a
    // tap handler that closes the upload sheet ~350ms earlier; that's well
    // within Chrome's 5-second transient activation window so the chooser
    // popup opens reliably without an extra click.
    const chosen = await chooseFromDropbox(allowMultiple);
    if (!chosen.length) return null;
    const results: PickedFile[] = [];
    for (const file of chosen) {
      try {
        const picked = await fetchDropboxFile(file);
        if (picked) results.push(picked);
      } catch (err) {
        debug.error('cloudPickers: failed to download Dropbox file', err as any);
      }
    }
    return results.length ? results : null;
  } catch (err) {
    debug.error('cloudPickers: Dropbox chooser failed', err as any);
    return null;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Open Google Drive picker / Files-app Drive location and return the chosen
 * files normalised as PickedFile[]. On web/desktop runs the OAuth+Picker
 * flow and fetches each file as a blob; on native delegates to the system
 * document picker (which surfaces Drive as a Files app location on iOS).
 */
export async function pickFromGoogleDrive(allowMultiple = false): Promise<PickedFile[] | null> {
  if (Platform.OS === 'web') return pickFromGoogleDriveWeb(allowMultiple);
  return pickFileDirect(allowMultiple);
}

/**
 * Open Dropbox chooser / Files-app Dropbox location and return the chosen
 * files normalised as PickedFile[]. On web/desktop runs the Dropbox Chooser
 * with linkType:'direct' and fetches each file as a blob; on native
 * delegates to the system document picker (which surfaces Dropbox as a
 * Files app location on iOS).
 */
export async function pickFromDropbox(allowMultiple = false): Promise<PickedFile[] | null> {
  if (Platform.OS === 'web') return pickFromDropboxWeb(allowMultiple);
  return pickFileDirect(allowMultiple);
}

// Test-only exports: internal web helpers exposed for unit tests
// (__tests__/cloudPickers.test.ts). Not part of the public picker API.
export const __cloudPickersTest = {
  fetchGoogleDriveDoc,
  openGooglePicker,
  pickFromGoogleDriveWeb,
  getDriveAccessTokenViaBackend,
};
